import React from "react";
import { Box, Text } from "@chakra-ui/layout";
import { Button, Collapse, useDisclosure } from "@chakra-ui/react";

import { Link } from "components/Link";
import { OrderSummaryV2 } from "components/OrderSummary";
import { Price } from "components/Price";
import { InfoIcon } from "components/SvgIcons";
import { useBasketCount } from "lib/apollo/hooks";
import { useUser } from "lib/user";
import { OrderSectionLine } from "components/OrderSummary/components/OrderSections/OrderSections.styles";
import { track, trackClick } from "tracking";
import { DeprecatedChakraBlackButton } from "packages/ui-shared/components/Button/Buttons.styles";

import { iconButton } from "./DrawerFooterWrapper.css";
import type { ModalCloseType } from "../ATBConfirmation";
import { useNMCustomerJourney } from "packages/acquisition/components/GuestPrice/hooks/useNMCustomerJourney";

type Props = {
  closeDrawer: (position: ModalCloseType) => void;
};

const BasketPricing = () => {
  const {
    pricing,
    hasOnlyVirtualProducts,
    basket,
    deliveryMethod,
    deliveryMethodName
  } = useUser();
  const { isOpen, onToggle } = useDisclosure();

  const isNMCustomerJourney = useNMCustomerJourney();

  if (!pricing) {
    return null;
  }

  const handleToggleWithTracking = () => {
    track({
      event: "Page Element Clicked",
      properties: {
        module_content: "ATB-Confirmation: Toggle price breakdown",
        location_type: "ATB_CONFIRMATION"
      }
    });
    onToggle();
  };

  const showEstimatedShippingCost = deliveryMethod === "None";
  const totalBasketPrice =
    showEstimatedShippingCost && !hasOnlyVirtualProducts && basket.length
      ? pricing.totalPrice.gross + pricing.estimatedShipping
      : pricing.totalPrice.gross;

  return (
    <Box
      display="flex"
      width="100%"
      flexDirection="column"
      cursor="pointer"
      mb={1}
      onClick={handleToggleWithTracking}
    >
      <Collapse in={isOpen} animateOpacity>
        <OrderSummaryV2
          title="Order summary"
          pricing={pricing}
          deliveryMethod={deliveryMethod}
          deliveryMethodName={deliveryMethodName}
          hasOnlyVirtualProducts={hasOnlyVirtualProducts ?? false}
          showTotalPrice={false}
          isCheckout
        />
        <OrderSectionLine />
      </Collapse>

      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection="row"
        width="100%"
        alignItems="center"
        mt={isOpen ? 2 : 0}
      >
        <Text mt={0}>
          {isNMCustomerJourney
            ? "Your total including shipping "
            : "Your bag total "}
          <button
            type="button"
            onClick={onToggle}
            className={iconButton}
            aria-label={`${isOpen ? "close" : "open"}-order-summary`}
          >
            <InfoIcon w="20px" h="24px" />
          </button>
        </Text>

        <Price
          price={totalBasketPrice}
          signSymbol=""
          variant="acuminBold"
          fontSize="24px"
          color="black"
          as="span"
        />
      </Box>
    </Box>
  );
};

export const DrawerFooterWrapper: React.FC<Props> = ({ closeDrawer }) => {
  const basketTotalItems = useBasketCount();

  return (
    <>
      {basketTotalItems ? (
        <>
          <BasketPricing />
          <Link href="/basket" width="100%">
            <DeprecatedChakraBlackButton
              variant="acuminRegular"
              mt={0}
              width="100%"
              onClick={() => {
                trackClick({
                  event: "Page Element Clicked",
                  properties: {
                    module_content: "ATB-Confirmation: Review bag & checkout",
                    location_type: "ATB_CONFIRMATION"
                  }
                });
              }}
            >
              Review bag & checkout
            </DeprecatedChakraBlackButton>
          </Link>

          <Button
            variant="link"
            mt={0}
            onClick={() => closeDrawer("Continue Shopping CTA")}
            data-testid="drawer-footer-continue-shopping-cta"
          >
            Continue Shopping
          </Button>
        </>
      ) : (
        <DeprecatedChakraBlackButton
          variant="acuminRegular"
          mt={0}
          width="100%"
          onClick={() => closeDrawer("Continue Shopping CTA")}
          data-testid="drawer-footer-continue-shopping-cta"
        >
          Continue Shopping
        </DeprecatedChakraBlackButton>
      )}
    </>
  );
};
